import React from 'react';
import { UserGroupIcon, UsersIcon } from '@heroicons/react/24/outline';

/**
 * ImpactBadge Component
 *
 * Displays business impact badges showing how many teams and users are affected by an issue.
 * Uses purple for teams and blue for users to align with stakeholder management color scheme.
 *
 * @param {Object} props
 * @param {Object} props.impactData - Business impact data from API
 * @param {number} props.impactData.teams_affected - Number of stakeholder groups affected
 * @param {number} props.impactData.users_affected - Number of users affected
 * @param {Array} props.impactData.stakeholder_groups - Array of affected stakeholder groups
 * @param {string} props.size - Badge size: 'sm', 'md', 'lg' (default: 'md')
 * @param {string} props.layout - Layout: 'horizontal', 'vertical' (default: 'horizontal')
 * @param {boolean} props.showTooltip - Whether to show detailed tooltip on hover (default: true)
 */
const ImpactBadge = ({
  impactData,
  size = 'md',
  layout = 'horizontal',
  showTooltip = true
}) => {
  // Return null if no impact data or no affected teams/users
  if (!impactData || (impactData.teams_affected === 0 && impactData.users_affected === 0)) {
    return null;
  }

  const { teams_affected, users_affected, stakeholder_groups } = impactData;

  // Size classes
  const sizeClasses = {
    sm: {
      badge: 'px-2 py-0.5 text-xs',
      icon: 'h-3 w-3',
      gap: 'gap-1'
    },
    md: {
      badge: 'px-2.5 py-1 text-sm',
      icon: 'h-4 w-4',
      gap: 'gap-1.5'
    },
    lg: {
      badge: 'px-3 py-1.5 text-base',
      icon: 'h-5 w-5',
      gap: 'gap-2'
    }
  };

  const classes = sizeClasses[size] || sizeClasses.md;

  // Layout classes
  const layoutClass = layout === 'vertical' ? 'flex-col gap-2' : 'flex-row gap-2';

  // Tooltip content
  const tooltipContent = showTooltip && stakeholder_groups && stakeholder_groups.length > 0 ? (
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-secondary-800 text-white text-xs rounded shadow-lg z-50 whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
      <div className="font-medium mb-1">Affected Stakeholder Groups:</div>
      {stakeholder_groups.map((group, index) => (
        <div key={index} className="flex items-center gap-2 py-0.5">
          <span className="font-semibold">{group.name}</span>
          <span className="text-secondary-400">({group.member_count} {group.member_count === 1 ? 'member' : 'members'})</span>
        </div>
      ))}
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-secondary-800"></div>
    </div>
  ) : null;

  return (
    <div className={`inline-flex items-center ${layoutClass} ${classes.gap} group relative`}>
      {/* Teams Badge */}
      {teams_affected > 0 && (
        <span className={`inline-flex items-center ${classes.badge} ${classes.gap} bg-purple-100 text-purple-700 rounded-full font-medium`}>
          <UserGroupIcon className={classes.icon} />
          <span>{teams_affected} {teams_affected === 1 ? 'team' : 'teams'}</span>
        </span>
      )}

      {/* Users Badge */}
      {users_affected > 0 && (
        <span className={`inline-flex items-center ${classes.badge} ${classes.gap} bg-blue-100 text-blue-700 rounded-full font-medium`}>
          <UsersIcon className={classes.icon} />
          <span>{users_affected} {users_affected === 1 ? 'user' : 'users'}</span>
        </span>
      )}

      {/* Tooltip */}
      {tooltipContent}
    </div>
  );
};

export default ImpactBadge;
